import { useState, useEffect } from 'react';
import { useCountries, useStates, useLgas } from '../../hooks/useGeoUnits';
import { useOrganization } from '../../hooks/useOrganizations';

export default function OrganizationModal({
    isOpen,
    onClose,
    onSave,
    editData,
    title,
    tenants = [],
    isLoading = false,
    isViewMode = false
}) {
    const [formData, setFormData] = useState({
        tenant_id: '',
        name: '',
        code: '',
        country_id: '',
        state_id: '',
        lga_id: '',
        address: '',
    });

    // Fetch full organization details when editing
    const { data: orgDetail } = useOrganization(editData?.id);

    // queries
    const { data: countriesData, isLoading: loadingCountries } = useCountries();
    const { data: statesData, isLoading: loadingStates } = useStates(formData.country_id);
    const { data: lgasData, isLoading: loadingLgas } = useLgas(formData.state_id);

    // Extract arrays from response
    const countries = countriesData?.results || countriesData || [];
    const states = statesData?.results || statesData || [];
    const lgas = lgasData?.results || lgasData || [];

    useEffect(() => {
        if (editData && isOpen) {
            // Priority: fetched detail > list data
            const detail = orgDetail || editData;
            const primaryLoc = detail.primary_location || {};

            setFormData({
                tenant_id: detail.tenant_id || detail.tenant?.id || detail.tenant || '',
                name: detail.name || '',
                code: detail.code || '',
                country_id: detail.country_id || primaryLoc.country_id || '',
                state_id: detail.state_id || primaryLoc.state_id || '',
                lga_id: detail.lga_id || primaryLoc.lga_id || '',
                address: detail.address || primaryLoc.address || '',
            });
        } else if (isOpen) {
            setFormData({
                tenant_id: '',
                name: '',
                code: '',
                country_id: '',
                state_id: '',
                lga_id: '',
                address: '',
            });
        }
    }, [editData, orgDetail, isOpen]);

    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData(prev => {
            const updated = { ...prev, [name]: value };

            // Reset child IDs if parent geography changes
            if (name === 'country_id') {
                updated.state_id = '';
                updated.lga_id = '';
            }
            if (name === 'state_id') {
                updated.lga_id = '';
            }

            return updated;
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Find selecting tenant to include its name in payload 
        const selectedTenant = tenants.find(t => t.id === formData.tenant_id);

        const submitData = {
            ...formData,
            tenant_name: selectedTenant?.name || ''
        };

        onSave(submitData);
    };

    if (!isOpen) return null;

    return (
        <>
            <div className="fixed p-15 w-full max-w-[700px] left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] z-[6]">
                <div className='py-10 md:px-10 px-[7px] bg-bg-dark rounded-lg shadow-light'>
                    <h5 className="text-[16px]/[19.2px] font-medium md:px-10 px-[7px] py-10 mb-20 text-center border-b border-border">
                        {title}
                    </h5>
                    <form onSubmit={handleSubmit}>
                        <div className='my-10 md:px-10 px-[7px] max-h-[65vh] overflow-auto cus-scrollbar'>
                            <div className='grid grid-cols-12 gap-20'>

                                {/* Tenant */}
                                <div className='form-group flex flex-col md:col-span-6 col-span-12'>
                                    <label className='mb-5 text-sm font-medium'>
                                        Tenant <span className='text-danger'>*</span>
                                    </label>
                                    <select
                                        name='tenant_id'
                                        value={formData.tenant_id}
                                        onChange={handleChange}
                                        className={`bg-bg-light border border-border w-full py-[6px] px-3 min-h-[40px] rounded-[4px] focus:outline-none ${isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                        required
                                        disabled={isLoading || isViewMode}
                                    >
                                        <option value=''>Select Tenant</option>
                                        {tenants.map(tenant => (
                                            <option key={tenant.id} value={tenant.id}>
                                                {tenant.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Organization Name */}
                                <div className='form-group flex flex-col md:col-span-6 col-span-12'>
                                    <label className='mb-5 text-sm font-medium'>
                                        Organization Name <span className='text-danger'>*</span>
                                    </label>
                                    <input
                                        type='text'
                                        name='name'
                                        value={formData.name}
                                        onChange={handleChange}
                                        placeholder='e.g., Global Enterprises'
                                        required
                                        disabled={isLoading || isViewMode}
                                        className={isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''}
                                    />
                                </div>

                                {/* Code */}
                                <div className='form-group flex flex-col md:col-span-6 col-span-12'>
                                    <label className='mb-5 text-sm font-medium'>
                                        Code <span className='text-danger'>*</span>
                                    </label>
                                    <input
                                        type='text'
                                        name='code'
                                        value={formData.code}
                                        onChange={handleChange}
                                        placeholder='e.g., EGG-001'
                                        required
                                        disabled={isLoading || isViewMode}
                                        className={isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''}
                                    />
                                </div>

                                {/* Country */}
                                <div className='form-group flex flex-col md:col-span-6 col-span-12'>
                                    <label className='mb-5 text-sm font-medium'>
                                        Country <span className='text-danger'>*</span>
                                    </label>
                                    <select
                                        name='country_id'
                                        value={formData.country_id}
                                        onChange={handleChange}
                                        required
                                        disabled={isLoading || loadingCountries || isViewMode}
                                        className={`bg-bg-light border border-border w-full py-[6px] px-3 min-h-[40px] rounded-[4px] focus:outline-none ${isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                    >
                                        <option value=''>{loadingCountries ? 'Loading...' : 'Select Country'}</option>
                                        {countries.map(country => (
                                            <option key={country.id} value={country.id}>
                                                {country.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* State */}
                                <div className='form-group flex flex-col md:col-span-6 col-span-12'>
                                    <label className='mb-5 text-sm font-medium'>
                                        State <span className='text-danger'>*</span>
                                    </label>
                                    <select
                                        name='state_id'
                                        value={formData.state_id}
                                        onChange={handleChange}
                                        required
                                        disabled={isLoading || !formData.country_id || loadingStates || isViewMode}
                                        className={`bg-bg-light border border-border w-full py-[6px] px-3 min-h-[40px] rounded-[4px] focus:outline-none ${isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                    >
                                        <option value=''>{loadingStates ? 'Loading...' : 'Select State'}</option>
                                        {states.map(state => (
                                            <option key={state.id} value={state.id}>
                                                {state.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* LGA */}
                                <div className='form-group flex flex-col md:col-span-6 col-span-12'>
                                    <label className='mb-5 text-sm font-medium'>
                                        LGA
                                    </label>
                                    <select
                                        name='lga_id'
                                        value={formData.lga_id}
                                        onChange={handleChange}
                                        className={`bg-bg-light border border-border w-full py-[6px] px-3 min-h-[40px] rounded-[4px] focus:outline-none ${isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                        disabled={isLoading || !formData.state_id || loadingLgas || isViewMode}
                                    >
                                        <option value=''>{loadingLgas ? 'Loading...' : 'Select LGA'}</option>
                                        {lgas.map(lga => (
                                            <option key={lga.id} value={lga.id}>
                                                {lga.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Address */}
                                <div className='form-group flex flex-col col-span-12'>
                                    <label className='mb-5 text-sm font-medium'>
                                        Address <span className='text-danger'>*</span>
                                    </label>
                                    <input
                                        type='text'
                                        name='address'
                                        value={formData.address}
                                        onChange={handleChange}
                                        placeholder='e.g., 12 Plot B, Alausa'
                                        required
                                        disabled={isLoading || isViewMode}
                                        className={isViewMode ? 'bg-gray-100 cursor-not-allowed' : ''}
                                    />
                                </div>

                            </div>
                        </div>
                        <div className='md:px-10 px-[7px] flex gap-10 justify-end py-10'>
                            <button
                                type='button'
                                onClick={onClose}
                                className='btn btn-outline-secondary'
                                disabled={isLoading}
                            >
                                {isViewMode ? 'Close' : 'Cancel'}
                            </button>
                            {!isViewMode && (
                                <button
                                    type='submit'
                                    className='btn btn-primary'
                                    disabled={isLoading}
                                >
                                    {isLoading ? 'Saving...' : (editData ? 'Update' : 'Submit')}
                                </button>
                            )}
                        </div>
                    </form>
                </div >
            </div >
            <div onClick={onClose} className='fixed z-[5] w-full h-full left-0 top-0 bg-bg-transparent backdrop-blur-[5px]'></div>
        </>
    );
}